import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, map } from 'rxjs/operators';
import { AutoUnsubscribe } from '../../common/decorators';
import { Subscription } from 'rxjs';
import { AnyFilterItem, FilterSettings } from './models';
import { parseChipsInputBulkDataEntry, removeInputElement, replaceSlash } from '../../common/utils';
import { ENTER } from '@angular/cdk/keycodes';
import { FilterType } from './constants';
import { IconNames } from '../../common/constants/icon-names';
import { ListQueryParam } from '../../common/constants/list-query-param.constant';
import { SearchCriteria } from '../../common';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { DateTimeRangeComponent } from '../date-time-range';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-list-filter',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatSelectModule,
    TranslateModule,
    IntrackFeatureModule,
    DateTimeRangeComponent,
    SvgIconComponent,
  ],
  templateUrl: './list-filter.component.html',
  styleUrls: ['./list-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class ListFilterComponent {
  @Input() set filtersConfig(config: FilterSettings) {
    if (config) {
      this.settings = config;
      this.initFilters();
    }
  }

  @Input() isTimeRangeResetAvailable = false;
  @Input() timezone: string | null = null;

  @Output() filtersChange: EventEmitter<SearchCriteria> = new EventEmitter<SearchCriteria>();

  filterType = FilterType;
  filtersForm: UntypedFormGroup;
  settings: FilterSettings;
  subscriptions: Subscription[] = [];
  chipsInputControl: UntypedFormControl = new UntypedFormControl('');
  separatorKeysCodes: number[] = [ENTER];
  iconNames = IconNames;
  removeElement = removeInputElement;

  constructor(public cd: ChangeDetectorRef) {}

  addElement(
    control: UntypedFormControl | AbstractControl,
    input: HTMLInputElement,
    inputControl: UntypedFormControl,
  ): void {
    parseChipsInputBulkDataEntry(control, input, inputControl);
  }

  private initFilters(): void {
    this.filtersForm = new UntypedFormGroup({});
    this.settings.items.forEach((filter: AnyFilterItem) => {
      this.filtersForm.addControl(
        filter.name,
        new UntypedFormControl({ value: filter.initialValue, disabled: filter.disabled }, []),
      );
    });
    this.subscriptions.push(this.formChangeSubscription());
  }

  private formChangeSubscription(): Subscription {
    return this.filtersForm.valueChanges
      .pipe(
        debounceTime(500),
        map((filterList: SearchCriteria) => this.checkAndUpdateFilterList(filterList)),
      )
      .subscribe((formValue: SearchCriteria) => {
        this.filtersChange.emit(formValue);
      });
  }

  private checkAndUpdateFilterList(filterList: SearchCriteria): SearchCriteria {
    Object.keys(filterList).forEach((value: ListQueryParam) => {
      if (typeof filterList[value] === 'string') {
        filterList[value] = replaceSlash(filterList[value]);
      }
    });
    return filterList;
  }
}
